import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import { memo, useEffect, useState } from "react";

import { getMediaUrl } from "@/configs";

import { DEFAULT_AVATAR_SIZE, DEFAULT_AVATAR_SRC } from "./data";

interface UserAvatarProps {
  /** The player's own picture (media key or URL); the mascot when unset. */
  avatar?: string | null;
  size?: number;
  alt?: string;
  sx?: SxProps<Theme>;
}

export const UserAvatar = memo(
  ({ avatar, size = DEFAULT_AVATAR_SIZE, alt = "profile", sx }: UserAvatarProps) => {
    const custom = typeof avatar === "string" ? getMediaUrl(avatar, size) : "";
    const [src, setSrc] = useState(custom || DEFAULT_AVATAR_SRC);

    useEffect(() => setSrc(custom || DEFAULT_AVATAR_SRC), [custom]);

    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        width={size}
        height={size}
        loading="lazy"
        // Google sign-up pictures (googleusercontent.com) can 403 when sent a referrer.
        referrerPolicy="no-referrer"
        // A broken upload falls back to the mascot instead of a broken-image icon.
        onError={() => setSrc(DEFAULT_AVATAR_SRC)}
        sx={[
          { objectFit: "cover", borderRadius: "50%", display: "block", flexShrink: 0 },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      />
    );
  },
);

UserAvatar.displayName = "User Avatar";
